// src/components/Photograph.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "./photograph.css";
import AudioFloat from "./AudioFloat.js";


/* ---------- helpers ---------- */
function ensureHttps(u) {
  if (!u) return u;
  const s = u.trim();
  if (s.startsWith("//")) return "https:" + s;
  if (s.startsWith("http://")) return "https://" + s.slice(7);
  return s;
}
function encodeSpaces(u) {
  if (!u) return u;
  // Only encode raw spaces; leave existing %20 intact
  return u.replace(/ /g, "%20");
}
function normalizeURL(u) {
  if (!u) return "";
  // trim, force https, encode spaces
  let out = encodeSpaces(ensureHttps(u.trim()));
  // remove accidental double slashes after protocol
  out = out.replace(/^https:\/\/+/, "https://").replace(/([^:])\/\/+/g, "$1/");
  return out;
}
function extSwapCandidates(url) {
  try {
    const u = new URL(url);
    const name = u.pathname.split("/").pop() || "";
    const dot = name.lastIndexOf(".");
    if (dot <= 0) return [url]; // no extension
    const base = name.slice(0, dot);
    const variants = new Set();
    // Keep original first
    variants.add(name);
    // Try common case/extension variants
    ["jpg", "jpeg", "JPG", "JPEG"].forEach((v) => variants.add(`${base}.${v}`));
    // Rebuild URLs
    return Array.from(variants).map((fname) => {
      const parts = u.pathname.split("/");
      parts[parts.length - 1] = fname;
      const rebuilt = `${u.origin}${parts.join("") ? parts.join("/") : u.pathname}`;
      return rebuilt;
    });
  } catch {
    return [url];
  }
}

export default function Photograph(props) {
  const { photograph, fetchPhotograph, match } = props;
  const id = match?.params?.id;

  const [selectedImage, setSelectedImage] = useState(null);
  const [mainURL, setMainURL] = useState(null);
  const [mainIsProp, setMainIsProp] = useState(false);

  // UI state
  const [showPrinting, setShowPrinting] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  // Frame toggle (persisted)
  const [showFrame, setShowFrame] = useState(() => {
    try {
      const saved = localStorage.getItem("lvl_frame_pref");
      return saved ? saved === "1" : true; // default ON
    } catch {
      return true;
    }
  });

  // error/debug state
  const [imgStatus, setImgStatus] = useState("idle"); // idle | loading | ok | error
  const [activeURL, setActiveURL] = useState("");

  // Reveal + image load classes driven by state (prevents disappearing on toggle)
  const [revealed, setRevealed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { setRevealed(true); }, []);

  useEffect(() => {
    if (!photograph || photograph._id !== id) fetchPhotograph?.(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photograph, id]);

  useEffect(() => {
    if (photograph) {
      const primary = photograph.imageURL || photograph.url || "";
      const normalized = normalizeURL(primary);
      setSelectedImage(normalized);
      setMainURL(normalized);
      setMainIsProp(false);
      setActiveURL(normalized);
      setImgStatus(normalized ? "loading" : "error");
    }
  }, [photograph]);

  useEffect(() => {
    try { localStorage.setItem("lvl_frame_pref", showFrame ? "1" : "0"); } catch {}
  }, [showFrame]);

  const seriesImages = useMemo(() => {
    if (!photograph) return [];
    const imgs = [
      photograph.image1,
      photograph.image2,
      photograph.image3,
      photograph.image4,
      photograph.image5,
      photograph.image6,
    ]
      .filter(Boolean)
      .map(normalizeURL);
    if (mainIsProp && mainURL) imgs.push(mainURL);
    return imgs.filter((u) => u && u !== (selectedImage || mainURL));
  }, [photograph, selectedImage, mainIsProp, mainURL]);

  function selectImage(image) {
    if (!image) return;
    if (image !== (selectedImage || mainURL) && image !== mainURL) {
      setSelectedImage(image);
      setActiveURL(image);
      setImgStatus("loading");
      setMainIsProp(true);
    } else if (image === mainURL && image !== selectedImage) {
      setSelectedImage(image);
      setActiveURL(image);
      setImgStatus("loading");
      setMainIsProp(false);
    }
  }

  // pricing rows
  const pricingRows = useMemo(() => {
    if (!photograph) return [];
    const rows = [];
    if (photograph.size1) {
      rows.push({
        size: photograph.size1,
        editionLimit: photograph.editionLimit1,
        price: photograph.price1 ? `$${photograph.price1}` : null,
      });
    }
    if (photograph.size2) {
      rows.push({
        size: photograph.size2,
        editionLimit: photograph.editionLimit2,
        price: photograph.price2 ? `$${photograph.price2}` : null,
      });
    }
    if (photograph.size3) {
      rows.push({
        size: photograph.size3,
        editionLimit: photograph.editionLimit3,
        price: photograph.price3 ? `$${photograph.price3}` : null,
      });
    }
        if (photograph.size4) {
      rows.push({
        size: photograph.size4,
        editionLimit: photograph.editionLimit4,
        price: photograph.price4 ? `$${photograph.price4}` : null,
      });
    }
    return rows;
  }, [photograph]);

  // candidates (try .jpg/.JPG/.jpeg if needed)
  const candidateURLs = useMemo(() => {
    const primary = normalizeURL(selectedImage || mainURL || photograph?.imageURL || photograph?.url || "");
    const variants = extSwapCandidates(primary);
    // Deduplicate and keep order
    const seen = new Set();
    const list = [];
    [primary, ...variants].forEach((u) => {
      const v = normalizeURL(u);
      if (v && !seen.has(v)) {
        seen.add(v);
        list.push(v);
      }
    });
    return list;
  }, [selectedImage, mainURL, photograph]);

  const [candidateIndex, setCandidateIndex] = useState(0);
  useEffect(() => { setCandidateIndex(0); }, [activeURL]);

  // Reset "loaded" flag whenever src changes so CSS can fade-in again
  const src = candidateURLs[candidateIndex] || "";
  useEffect(() => { setImgLoaded(false); }, [src]);

  const onImgLoad = useCallback(() => {
    setImgStatus("ok");
    setImgLoaded(true);
  }, []);

  const onImgError = useCallback(() => {
    const next = candidateIndex + 1;
    if (next < candidateURLs.length) {
      setCandidateIndex(next);
      const nextURL = candidateURLs[next];
      setSelectedImage(nextURL);
      setActiveURL(nextURL);
      setImgStatus("loading");
    } else if (seriesImages.length) {
      const alt = seriesImages[0];
      setSelectedImage(alt);
      setActiveURL(alt);
      setImgStatus("loading");
    } else {
      setImgStatus("error");
    }
  }, [candidateIndex, candidateURLs, seriesImages]);

  if (!photograph) {
    return (
      <div className="main-body fade-in">
        <div className="main-page pad-top-50 smaller uppercase black">
          <Link to="/shop/portraits" className="back-btn">‹ Back to Portraits</Link>
          <div className="loading">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-body fade-in photograph-detail">
      <div className="main-page pad-top-50">
      

        <section className="photograph-layout">
          {/* LEFT: Details (centered via CSS) */}
          <aside className="photograph-left" aria-label="Artwork details">
            <header className="photograph-header">
              {photograph.series && <div className="series overline">{photograph.series}</div>}
              <h1 className="title-serif">{photograph.title || "Untitled"}</h1>
              <div className="meta">
                {photograph.location && <div className="meta-row-location">{photograph.location}</div>}
                {(photograph.year || photograph.edition) && (
                  <div className="meta-row">
                    <br/>
                    {photograph.year && <span className="muted">{photograph.year}</span>}
                    {photograph.year && photograph.edition && <span className="dot" />}
                    {photograph.edition && <span className="muted">Edition {photograph.edition}</span>}
                  </div>
                )}<br/>
                {photograph.medium && <div className="meta-row italic">Medium: {photograph.medium}</div>}
              </div>
            </header>

            {photograph.description && (
              <section className="artist-statement" aria-label="Artist statement">
                <blockquote className="statement-quote">{photograph.description}</blockquote>
                {(photograph.statementAttribution || photograph.location || photograph.year) && (
                  <div className="statement-meta">— LVL</div>
                )}
              </section>
            )}

            {/* Controls: Frame toggle */}
            <div className="photographs-controls">
              <label className="frame-toggle">
                <input
                  type="checkbox"
                  checked={showFrame}
                  onChange={(e) => setShowFrame(e.target.checked)}
                />
                <span>Show frame</span>
              </label>
            </div>

            {/* Printing details */}
            <div className="accordion">
              <button
                className={`acc-head ${showPrinting ? "open" : ""}`}
                onClick={() => setShowPrinting((s) => !s)}
                aria-expanded={showPrinting}
              >
                <span>Printing details</span>
                <span className="chev" aria-hidden>▾</span>
              </button>
              {showPrinting && (
                <div className="acc-body">
                  {photograph.printingDetails && <p className="body-text">{photograph.printingDetails}</p>}
                  {photograph.disclosure && <p className="body-text muted">{photograph.disclosure}</p>}
                </div>
              )}
            </div>

            {/* Pricing details */}
            <div className="accordion">
              <button
                className={`acc-head ${showPricing ? "open" : ""}`}
                onClick={() => setShowPricing((s) => !s)}
                aria-expanded={showPricing}
              >
                <span>Pricing details</span>
                <span className="chev" aria-hidden>▾</span>
              </button>
              {showPricing && (
                <div className="acc-body">
                  {pricingRows.length ? (
                    <table className="price-table">
                      <thead>
                        <tr>
                          <th>Size</th>
                          <th>Edition limit</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingRows.map((r, i) => (
                          <tr key={i}>
                            <td>{r.size || "—"}</td>
                            <td>{r.editionLimit || "—"}</td>
                            <td>{r.price || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="body-text muted">Pricing to be confirmed.</p>
                  )}

                  {photograph.artistProof && (
                    <p className="body-text ap">
                      <strong>Artist Proof:</strong> {String(photograph.artistProof)}
                      {photograph.artistProofPricing && <> <span className="muted">· {photograph.artistProofPricing}</span></>}
                    </p>
                  )}
                </div>
              )}
            </div>

         <div className="inquire-wrap">
  <a
    className="inquire-btn"
    href={`mailto:contact@lorranlima.com?subject=Inquiry: ${encodeURIComponent(
      photograph.title || "Photograph"
    )} (${photograph._id})&body=Hi—I'm interested in this piece. Please share availability and pricing.`}
  >
    Reserve this piece
  </a>
</div>

            {/* Debug panel (hidden by default) */}
            {imgStatus !== "ok" && (
              <div className="body-text muted hidden" style={{ marginTop: 12 }}>
                <div><strong>Image status:</strong> {imgStatus}</div>
                {activeURL && (
                  <div style={{ wordBreak: "break-all" }}>
                    <strong>URL:</strong>{" "}
                    <a href={activeURL} target="_blank" rel="noreferrer">{activeURL}</a>
                    <button
                      style={{ marginLeft: 8, border: "1px solid #ddd", borderRadius: 6, padding: "2px 8px", cursor: "pointer" }}
                      onClick={() => navigator.clipboard?.writeText(activeURL)}
                    >
                      Copy
                    </button>
                  </div>
                )}
                {candidateURLs.length > 1 && (
                  <div>Trying {candidateIndex + 1} of {candidateURLs.length} candidates…</div>
                )}
              </div>
            )}
          </aside>

          {/* RIGHT: Image */}
          <div className="photograph-right">
            <figure
              className={[
                "lead-image-card",
                "ph-card",
                "reveal",
                revealed ? "is-visible" : "",
                showFrame ? "has-frame" : "",
              ].join(" ").trim()}
            >
              <div className="ph-media">
                {src ? (
                  <img
                    className={["ph-img", "detail-image", imgLoaded ? "is-loaded" : ""].join(" ")}
                    src={src}
                    alt={photograph.title || "Photograph"}
                    loading="eager"
                    decoding="async"
                    onLoad={onImgLoad}
                    onError={onImgError}
                  />
                ) : (
                  <div className="img-fallback">No image URL</div>
                )}
              </div>

              {seriesImages.length > 0 && (
                <div className="thumb-row">
                  {seriesImages.map((u, i) => (
                    <button
                      key={u + i}
                      type="button"
                      className="thumb-btn"
                      onClick={() => selectImage(u)}
                      aria-label={`Select variation ${i + 1}`}
                    >
                      <img className="thumb" src={u} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </figure>
          </div>
        </section>
      </div>

            {/* Audio player */}
            <AudioFloat
              srcs={[
                {
                  src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/shop.mp3",
                  type: "audio/mpeg",
                },
              ]}
              startMuted={false}
              startVolume={0.5}
              loop={true}
              label="Site background audio"
              startTime={190}
            />
    </div>
  );
}
